/**
 * pp-cart-drawer — modal con carrito + recomendaciones tras add-to-cart.
 *
 * Listens for `pp:cart-drawer:open` custom event (dispatched from
 * pp-main-product.js when the PDP form is submitted via AJAX).
 *
 * Detail: { productId, addedItem? }
 *
 * RECS RENDERING — server-rendered cards via Section Rendering:
 *   1. /recommendations/products.json gives the list of related products
 *      (handles, ids, prices). We use it only to know WHICH products
 *      to recommend.
 *   2. For each visible rec, we fetch its rendered <pp-product-card>
 *      from `/products/<handle>?view=cart-drawer-card&section_id=pp-cart-drawer-card`
 *      so the card has identical features to the collection grid (tier
 *      pills, qty stepper, add-to-cart, all metafields). Parallel fetch.
 *   3. window.PpInitCard(card) wires up qty stepper / tier pills / form
 *      submit on the freshly injected DOM.
 *
 * The injected card's add-to-cart form is intercepted by the global
 * pp-product-card.js submit handler (AJAX → /cart/add.js). That handler
 * dispatches `pp:cart-updated`. We listen and refresh the cart panel +
 * filter the just-added product out of the recs.
 */
(function () {
  'use strict';

  var VISIBLE_RECS = 4;
  var FETCH_RECS = 12;
  var CARD_VIEW = 'cart-drawer-card';
  var CARD_SECTION_ID = 'pp-cart-drawer-card';

  function init() {
    var root = document.querySelector('[data-pp-cart-drawer]');
    if (!root) return;
    new PpCartDrawer(root);
  }

  function PpCartDrawer(root) {
    this.root = root;
    this.panel = root.querySelector('.pp-cart-drawer__panel');
    this.recsListEl = root.querySelector('[data-pp-cart-drawer-recs]');
    this.recsEmptyEl = root.querySelector('[data-pp-cart-drawer-recs-empty]');
    this.cartItemsEl = root.querySelector('[data-pp-cart-drawer-items]');
    this.cartTotalEl = root.querySelector('[data-pp-cart-drawer-total]');

    this.cart = null;
    // allRecs: full ordered list of candidates [{id, handle}, ...]. NOT
    // drained on render — we just filter against the current cart each
    // time. Mutating this on every render was a bug: when the user added
    // one rec, the others vanished because the buffer got emptied.
    this.allRecs = [];
    this.cardHtmlCache = {}; // handle → rendered card HTML
    this.recsCurrentId = null;
    this.lastAddedVariantId = null;
    this.lastFocus = null;

    var self = this;

    document.addEventListener('pp:cart-drawer:open', function (e) {
      self.open((e && e.detail) || {});
    });

    // When ANY cart change happens (e.g. user adds a rec from inside the
    // drawer via the global pp-product-card submit handler), refresh.
    document.addEventListener('pp:cart-updated', function (e) {
      if (self.root.hidden) return;
      if (e && e.detail) {
        self.cart = e.detail;
      }
      self.refreshAfterCartChange();
    });

    var closeEls = root.querySelectorAll('[data-pp-cart-drawer-close]');
    for (var i = 0; i < closeEls.length; i++) {
      closeEls[i].addEventListener('click', function () { self.close(); });
    }

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !root.hidden) self.close();
    });

    // Reopen cuando el productModal del Paco se cierra (vino del bridge).
    // El user navegó del drawer al modal y al cerrar quiere volver al
    // drawer donde estaba, no a la PDP. recsCurrentId + allRecs siguen
    // en memoria (close() solo hide, no resetea) → re-render directo.
    document.addEventListener('pp:product-modal-closed', function (e) {
      if (!e || !e.detail || !e.detail.fromCartDrawer) return;
      self.open({ productId: self.recsCurrentId });
    });

    // Bridge mobile: tap en una rec card (cover o título) cierra el
    // drawer y abre el modal de producto del Paco con el mismo handle.
    // El "+" del overlay (.pp-prod-card__add) NO se intercepta — sigue
    // siendo submit del form que agrega 1 al carrito. Desktop conserva
    // el comportamiento default (navegación a /products/<handle>).
    if (this.recsListEl) {
      this.recsListEl.addEventListener('click', function (e) {
        if (!window.matchMedia('(max-width: 749px)').matches) return;
        // Si tocaron el botón "+" o algo dentro del form, dejamos pasar.
        if (e.target.closest('.pp-prod-card__form')) return;
        var link = e.target.closest('.pp-prod-card__cover, .pp-prod-card__title-link');
        if (!link) return;
        var href = link.getAttribute('href') || '';
        var match = href.match(/\/products\/([^/?#]+)/);
        if (!match) return;
        e.preventDefault();
        var handle = match[1];
        self.close();
        document.dispatchEvent(new CustomEvent('pp:open-product-modal', {
          detail: { handle: handle },
        }));
      });
    }
  }

  PpCartDrawer.prototype.open = function (detail) {
    this.lastFocus = document.activeElement;
    this.lastAddedVariantId = detail && detail.addedItem ? detail.addedItem.variant_id : null;

    this.root.hidden = false;
    document.body.classList.add('pp-cart-drawer-open');

    // El ancla (el producto del que se recomienda): el que nos digan, o el
    // del renglón recién agregado. Si nadie lo dice —Proyecciones, Compra
    // Rápida, el «Carrito» flotante, Repetir orden…— se toma el ÚLTIMO que
    // entró al carrito (ver anclaDelCarrito). Antes salía «No tenemos
    // recomendaciones» (Padrino, 7-oct-2026, captura 51: «todos los productos
    // tienen que tener recomendaciones»).
    var anchorId = (detail && (detail.productId || (detail.addedItem && detail.addedItem.product_id))) || null;

    var self = this;
    // Recomendaciones SIEMPRE del motor unificado (pp-recommendations): hace la
    // cascada completa (co-compra del CRM → reglas por categoria → novedad →
    // similares). El motor necesita el HANDLE del producto ancla; lo resolvemos
    // del cart, que refreshCart trae con handle por line item. Encadenamos para
    // que el cart este listo antes de calcular las recs.
    this.refreshCart().then(function () {
      var ancla = anchorId || self.anclaDelCarrito();
      if (ancla && ancla === self.recsCurrentId && self.allRecs.length > 0) {
        self.renderVisibleRecs();
        return;
      }
      self.recsCurrentId = ancla || self.recsCurrentId;
      self.recsConRespaldo(ancla).then(function () {
        self.renderVisibleRecs();
      });
    });

    if (this.panel) {
      try { this.panel.focus({ preventScroll: true }); } catch (e) {}
    }
  };

  PpCartDrawer.prototype.close = function () {
    this.root.hidden = true;
    document.body.classList.remove('pp-cart-drawer-open');
    if (this.lastFocus && typeof this.lastFocus.focus === 'function') {
      try { this.lastFocus.focus({ preventScroll: true }); } catch (e) {}
    }
  };

  PpCartDrawer.prototype.refreshCart = function () {
    var self = this;
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        self.cart = cart;
        self.renderCart();
      })
      .catch(function () { /* silent */ });
  };

  // Called after pp:cart-updated fires (e.g. when user added a rec).
  PpCartDrawer.prototype.refreshAfterCartChange = function () {
    var self = this;
    var p = this.cart ? Promise.resolve() : this.refreshCart();
    p.then(function () {
      self.renderCart();
      self.renderVisibleRecs();
    });
  };

  // Hit /recommendations/products.json to learn WHICH products to recommend.
  // We only use ids and handles — the rendered card HTML comes from the
  // section-rendering fetch in renderVisibleRecs().
  // Trae la lista ORDENADA de recomendaciones del motor unificado
  // (pp-recommendations). Solo usamos id + handle; el HTML de cada card lo trae
  // renderVisibleRecs via section-rendering (cart-drawer-card). El id es el
  // product_id (numeric) para el filtro "no en cart".
  PpCartDrawer.prototype.fetchRecsList = function (productHandle) {
    var self = this;
    this.cardHtmlCache = {};
    if (!productHandle) {
      this.allRecs = [];
      return Promise.resolve();
    }
    var url = '/products/' + encodeURIComponent(productHandle) +
      '?section_id=pp-recommendations&limit=' + FETCH_RECS;
    return fetch(url, { credentials: 'same-origin', headers: { Accept: 'text/html' } })
      .then(function (r) { return r.ok ? r.text() : ''; })
      .then(function (text) {
        var doc = new DOMParser().parseFromString(text, 'text/html');
        var cards = doc.querySelectorAll('.pp-prod-card');
        var recs = [];
        for (var i = 0; i < cards.length; i++) {
          var btn = cards[i].querySelector('[data-pp-wishlist-product-id]');
          var link = cards[i].querySelector('a[href*="/products/"]');
          var id = btn && parseInt(btn.getAttribute('data-pp-wishlist-product-id'), 10);
          var m = link && link.getAttribute('href').match(/\/products\/([a-z0-9\-]+)/);
          if (id && m) recs.push({ id: id, handle: m[1] });
        }
        self.allRecs = recs;
      })
      .catch(function () { self.allRecs = []; });
  };

  // El producto ancla cuando nadie lo dijo: el ÚLTIMO que entró al carrito
  // (Shopify pone primero el renglón más reciente en /cart.js). Se salta el
  // cobro de personalización, que no es un producto que se recomiende.
  PpCartDrawer.prototype.anclaDelCarrito = function () {
    var items = (this.cart && this.cart.items) || [];
    for (var i = 0; i < items.length; i++) {
      if (!/personalizaci/i.test(items[i].product_type || '')) return items[i].product_id;
    }
    return null;
  };

  // Recomendaciones del ancla y, si el motor no devuelve NADA que no esté ya
  // en el carrito, de los otros productos del carrito (hasta 3 intentos):
  // nunca «No tenemos recomendaciones» mientras haya de dónde sacar.
  PpCartDrawer.prototype.recsConRespaldo = function (ancla) {
    var self = this;
    var items = (this.cart && this.cart.items) || [];
    var ids = [];
    if (ancla) ids.push(ancla);
    items.forEach(function (it) {
      if (ids.indexOf(it.product_id) === -1 && !/personalizaci/i.test(it.product_type || '')) ids.push(it.product_id);
    });
    ids = ids.slice(0, 3);
    var enCarrito = {};
    items.forEach(function (it) { enCarrito[it.product_id] = true; });
    var hayAlgo = function () { return self.allRecs.some(function (r) { return !enCarrito[r.id]; }); };
    var probar = function (n) {
      if (n >= ids.length) return Promise.resolve();
      return self.fetchRecsList(self.handleForProductId(ids[n])).then(function () {
        if (hayAlgo()) return;
        return probar(n + 1);
      });
    };
    if (!ids.length) { self.allRecs = []; return Promise.resolve(); }
    return probar(0);
  };

  // Resuelve el handle de un product_id buscando en el cart (los line items
  // traen handle). El producto ancla siempre esta en el cart (se acaba de
  // agregar o ya estaba). null si no lo encuentra → recs vacias (degradacion).
  PpCartDrawer.prototype.handleForProductId = function (pid) {
    if (!pid || !this.cart || !this.cart.items) return null;
    for (var i = 0; i < this.cart.items.length; i++) {
      if (this.cart.items[i].product_id === pid) return this.cart.items[i].handle;
    }
    return null;
  };

  // Render up to VISIBLE_RECS cards. Filter from allRecs each call —
  // never mutate allRecs. Card HTML cached per handle so re-renders
  // (e.g. after pp:cart-updated) don't re-fetch already-fetched cards.
  PpCartDrawer.prototype.renderVisibleRecs = function () {
    var self = this;
    var inCart = {};
    if (this.cart && this.cart.items) {
      for (var i = 0; i < this.cart.items.length; i++) {
        inCart[this.cart.items[i].product_id] = true;
      }
    }

    // Filter allRecs (preserving order) to first N not in cart.
    var visible = [];
    for (var j = 0; j < this.allRecs.length && visible.length < VISIBLE_RECS; j++) {
      var p = this.allRecs[j];
      if (!inCart[p.id]) visible.push(p);
    }

    if (visible.length === 0) {
      this.recsListEl.replaceChildren();
      if (this.recsEmptyEl) this.recsEmptyEl.hidden = false;
      return;
    }
    if (this.recsEmptyEl) this.recsEmptyEl.hidden = true;

    // Show skeleton placeholders for any cards we still need to fetch.
    this.recsListEl.replaceChildren();
    for (var s = 0; s < visible.length; s++) {
      var skel = document.createElement('li');
      skel.className = 'pp-cart-drawer__rec-skel';
      this.recsListEl.appendChild(skel);
    }

    // Parallel fetch (or use cache) for each visible.
    Promise.all(visible.map(function (c) {
      if (self.cardHtmlCache[c.handle]) {
        return Promise.resolve({ handle: c.handle, html: self.cardHtmlCache[c.handle] });
      }
      return self.fetchCardHtml(c.handle).then(function (html) {
        if (html) self.cardHtmlCache[c.handle] = html;
        return { handle: c.handle, html: html };
      });
    })).then(function (results) {
      self.recsListEl.replaceChildren();
      for (var k = 0; k < results.length; k++) {
        if (!results[k].html) continue;
        var li = self.buildRecLi(results[k].html);
        if (!li) continue;
        self.recsListEl.appendChild(li);
      }
      // Wire up qty steppers, tier pills, AJAX submit on injected cards.
      if (typeof window.PpInitCard === 'function') {
        var cards = self.recsListEl.querySelectorAll('[data-pp-card]');
        for (var c = 0; c < cards.length; c++) {
          try { window.PpInitCard(cards[c]); } catch (e) {}
        }
      }
    });
  };

  PpCartDrawer.prototype.fetchCardHtml = function (handle) {
    if (!handle) return Promise.resolve('');
    var url =
      '/products/' + encodeURIComponent(handle) +
      '?view=' + encodeURIComponent(CARD_VIEW) +
      '&section_id=' + encodeURIComponent(CARD_SECTION_ID);
    return fetch(url, { headers: { Accept: 'text/html' } })
      .then(function (r) { return r.ok ? r.text() : ''; })
      .catch(function () { return ''; });
  };

  // Wraps the section HTML into the same <li> we use for the grid.
  PpCartDrawer.prototype.buildRecLi = function (html) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var article = doc.querySelector('[data-pp-card]');
    if (!article) return null;
    var li = document.createElement('li');
    li.className = 'pp-cart-drawer__rec';
    li.appendChild(article);
    return li;
  };

  // Cart side — built with createElement to avoid XSS via innerHTML.
  // ── EL RENGLÓN DEL COBRO DE UNA PERSONALIZACIÓN (R4) ─────────────────────
  //
  // Desde R4 una personalización son DOS líneas: la caja y su cobro. El cobro
  // se pinta como SUBRENGLÓN de su caja — sin foto ni link a un producto —
  // porque no es algo que el cliente escogió en la tienda: es lo que paga por
  // imprimir su diseño. Su nombre es el MISMO del checkout (6-oct-2026):
  // «Impresión de tu diseño · 1 cara · 25 cajas de …» o «Tinta extra (2.ª
  // tinta) · 40 rollos». El de catálogo, "Servicio de Personalización", no le
  // dice nada a nadie.
  //
  // La marca es `_ps_servicio`, explícita. Deducirla ("trae _design_id pero no
  // firma") se rompería callada el día que alguien agregue otro atributo.
  function esRenglonDeCobro(it) {
    return !!(it && it.properties && it.properties._ps_servicio);
  }

  // 5-oct-2026: con su caja al lado, el cobro dice DE QUÉ es —«Impresión de tu
  // diseño · 2 caras · 50 cajas de 26 × 13 × 14 cm», la etiqueta de assets/pp-personalizacion.js—
  // y lleva la imagen del diseño (`_mockup_url` de la caja), pegado debajo de
  // ella (renderCart lo pinta después de su caja, no donde Shopify lo deja).
  PpCartDrawer.prototype.buildCobroEl = function (it, caja) {
    var li = document.createElement('li');
    var mockup = caja && caja.properties && caja.properties._mockup_url;
    li.className = 'pp-cart-drawer__cart-item pp-cart-drawer__cart-item--cobro' + (mockup ? ' pp-cart-drawer__cart-item--de-su-caja' : '');
    if (mockup) {
      var img = document.createElement('img');
      img.className = 'pp-cart-drawer__cart-img pp-cart-drawer__cart-img--diseno';
      img.src = String(mockup);
      img.alt = '';
      img.loading = 'lazy';
      li.appendChild(img);
    }

    var info = document.createElement('div');
    info.className = 'pp-cart-drawer__cart-info';
    var name = document.createElement('span');
    name.className = 'pp-cart-drawer__cart-name';
    // `it.title` es el título de la LÍNEA (el que pone el transform);
    // `product_title` sería el de catálogo.
    var P = window.PPPersonalizacion;
    name.textContent = caja && P ? P.etiquetaDelCobro(it, caja) : (it.title || 'Personalización');
    info.appendChild(name);
    var meta = document.createElement('p');
    meta.className = 'pp-cart-drawer__cart-meta';
    // «por rollo» en la cinta, «por caja» en la caja: la unidad del nombre.
    meta.textContent = formatMoney(it.final_price) + ' por ' + (caja && P && P.unidadDelCobro ? P.unidadDelCobro(caja) : 'pieza');
    info.appendChild(meta);
    li.appendChild(info);

    var lineTotal = document.createElement('strong');
    lineTotal.className = 'pp-cart-drawer__cart-line-total';
    var totalAmt = document.createElement('span');
    totalAmt.textContent = formatMoney(it.final_line_price);
    lineTotal.appendChild(totalAmt);
    li.appendChild(lineTotal);
    return li;
  };

  PpCartDrawer.prototype.buildCartItemEl = function (it, justAdded) {
    if (esRenglonDeCobro(it)) return this.buildCobroEl(it);
    var li = document.createElement('li');
    li.className = 'pp-cart-drawer__cart-item' + (justAdded ? ' pp-cart-drawer__cart-item--just-added' : '');

    if (it.image) {
      var img = document.createElement('img');
      img.className = 'pp-cart-drawer__cart-img';
      img.src = String(it.image).replace(/\.(jpg|jpeg|png|webp)/i, '_120x.$1');
      img.alt = it.product_title || '';
      img.loading = 'lazy';
      li.appendChild(img);
    } else {
      var ph = document.createElement('div');
      ph.className = 'pp-cart-drawer__cart-img';
      ph.setAttribute('aria-hidden', 'true');
      li.appendChild(ph);
    }

    var info = document.createElement('div');
    info.className = 'pp-cart-drawer__cart-info';
    var name = document.createElement('a');
    name.className = 'pp-cart-drawer__cart-name';
    name.href = it.url || '#';
    // El GRABADO de una cinta (TINTA-01 × tintas) dice qué es y que no se repite.
    var PG = window.PPPersonalizacion;
    name.textContent = it.properties && it.properties._ps_grabado_de && PG && PG.etiquetaDelGrabado
      ? PG.etiquetaDelGrabado(it)
      : (it.product_title || '');
    info.appendChild(name);

    var hasDiscount =
      it.original_line_price != null &&
      it.final_line_price != null &&
      it.original_line_price > it.final_line_price;

    var meta = document.createElement('p');
    meta.className = 'pp-cart-drawer__cart-meta';
    meta.textContent = (it.quantity || 0) + ' × ' + formatMoney(it.final_price);
    info.appendChild(meta);
    li.appendChild(info);

    // Line total with optional struck-through original below.
    // Keep it 2 lines max — the "savings" green line was too noisy.
    var lineTotal = document.createElement('strong');
    lineTotal.className = 'pp-cart-drawer__cart-line-total';
    var totalAmt = document.createElement('span');
    totalAmt.textContent = formatMoney(it.final_line_price);
    lineTotal.appendChild(totalAmt);
    if (hasDiscount) {
      var origLine = document.createElement('span');
      origLine.className = 'pp-cart-drawer__cart-line-original';
      origLine.textContent = formatMoney(it.original_line_price);
      lineTotal.appendChild(origLine);
    }
    li.appendChild(lineTotal);

    return li;
  };

  PpCartDrawer.prototype.renderCart = function () {
    if (!this.cart) return;
    var items = this.cart.items || [];
    this.cartItemsEl.replaceChildren();
    if (items.length === 0) {
      var empty = document.createElement('li');
      empty.className = 'pp-cart-drawer__cart-empty';
      empty.textContent = 'Tu carrito está vacío.';
      this.cartItemsEl.appendChild(empty);
      this.cartTotalEl.textContent = formatMoney(0);
      return;
    }
    // Cada cobro apareado va justo DEBAJO de su caja: Shopify lista el carrito
    // al revés de como se agregó y lo dejaba arriba, como renglón suelto.
    var P = window.PPPersonalizacion;
    var par = P ? P.aparear(items) : { cobroDe: {}, cajaDe: {} };
    for (var i = 0; i < items.length; i++) {
      if (par.cajaDe[items[i].key]) continue;
      var justAdded = items[i].variant_id === this.lastAddedVariantId;
      this.cartItemsEl.appendChild(this.buildCartItemEl(items[i], justAdded));
      var suCobro = par.cobroDe[items[i].key];
      if (suCobro) this.cartItemsEl.appendChild(this.buildCobroEl(suCobro, items[i]));
    }
    this.cartTotalEl.textContent = formatMoney(this.cart.total_price);
    this.lastAddedVariantId = null;
  };

  // Fisher-Yates shuffle in place
  function shuffle(arr) {
    for (var i = arr.length - 1; i > 0; i--) {
      var j = Math.floor(Math.random() * (i + 1));
      var t = arr[i]; arr[i] = arr[j]; arr[j] = t;
    }
    return arr;
  }

  function formatMoney(cents) {
    var n = (parseInt(cents, 10) || 0) / 100;
    var hasDecimals = (Math.round(n * 100) % 100) !== 0;
    return '$' + n.toLocaleString('es-MX', {
      minimumFractionDigits: hasDecimals ? 2 : 0,
      maximumFractionDigits: 2
    }) + ' MXN';
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
